<!-- GanttChartCanvas.vue -->
<template>
  <div class="gantt-chart">
    <div class="f aic jcsb">
      <div class="mb-8 f aic">
        <div>Выполнение задач</div>

        <TirPmPopover
          ref="$popoverJobFilter"
          :custom-position="{ left: `0rem`, top: `$el:height` }"
          width="400"
          @close="onClosePopoverJobFilter"
          @open="onOpenPopoverJobFilter"
        >
          <template #default>
            <FunnelIcon class="ml-4" style="width: 1rem" />
          </template>
          <template #content>
            <TirPmSearchInput
              style="width: 100%"
              v-model="searchJob"
              @update:modelValue="onSearchJob"
              :is-with-popup="false"
              label="Поиск по названию"
              placeholder=""
            />
            <PopoverOptionList>
              <PopoverOptionLi
                @click.stop="onSelectJob"
                v-for="el in selectedJobs"
                :key="el.JobId"
              >
                <TirCheckbox v-model="el.IsSelect" :key="el.JobId">
                  <template #mainSign>{{ el.JobName }}</template>
                </TirCheckbox>
              </PopoverOptionLi>
            </PopoverOptionList>

            <TirPmScrollbar
              :show-on-hover="true"
              :style="{ 'max-height': '30rem' }"
            >
              <PopoverOptionList>
                <PopoverOptionLi
                  @click.stop="onSelectJob(item)"
                  v-for="item in noSelectJobs"
                  :key="item.JobId"
                >
                  <TirCheckbox :value="item.IsSelect" :key="item.JobId">
                    <template #mainSign>{{ item.JobName }}</template>
                  </TirCheckbox>
                </PopoverOptionLi>
              </PopoverOptionList>
            </TirPmScrollbar>
          </template>
        </TirPmPopover>
      </div>

      <div class="f aic mb-4">
        <TirCheckbox
          style="margin-right: 0.5rem"
          v-model:model-value="onlyError"
          @update:model-value="onOnlyError"
        >
          <template #mainSign>Показать только с ошибками</template>
        </TirCheckbox>
        <TirPmButton :size="TirPmButtonSizeEnum.Small" @click="zoomOut">
          <template #leftSlot><MinusIcon /></template>
        </TirPmButton>
        <span class="zoom-level">{{ Math.round(zoomLevel * 100) }}%</span>
        <TirPmButton :size="TirPmButtonSizeEnum.Small" @click="zoomIn">
          <template #leftSlot><PlusIcon /></template>
        </TirPmButton>
      </div>
    </div>
    <div class="gantt-wrapper" ref="wrapper">
      <canvas
        ref="canvas"
        @mousemove="handleMouseMove"
        @mouseleave="handleMouseLeave"
        @wheel="handleWheel"
        @click="handleClick"
      />
      <div v-if="hoveredTask" class="tooltip" :style="tooltipStyle">
        <span
          class="tooltip-color"
          :style="{
            backgroundColor: STATUS_COLORS[hoveredTask.Status] || '#ccc',
          }"
        ></span>
        {{ getStatusName(hoveredTask.Status) }}:
        {{ hoveredTask.JobName || "test" }}
      </div>
    </div>
  </div>
  <div class="resize-handle mt-8" @mousedown="startResize"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick, computed } from "vue";
import {
  TirCheckbox,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmPopover,
  TirPmScrollbar,
} from "tir-components";
import EnumJobStatus from "@/api/SchedulerAdmin/dataSource/api/scheduler/manager/serverless/database/enums/EnumJobStatus";
import JobTimelineItem from "@/api/SchedulerAdmin/dataSource/api/scheduler/manager/serverless/i-services/models/dashboard/JobTimelineItem";
import JobTimeline from "@/api/SchedulerAdmin/dataSource/api/scheduler/manager/serverless/i-services/models/dashboard/JobTimeline";
import {
  MinusIcon,
  PlusIcon,
  FunnelIcon,
} from "tir-style-system/icons/outline";
import PopoverOptionList from "@/components/sidebar/components/popover-option-list/popover-option-list.vue";
import PopoverOptionLi from "@/components/sidebar/components/popover-option-list/popover-option-li.vue";
import TirPmSearchInput from "tir-pm-search-input/src/TirPmSearchInput.vue";

const STATUS_COLORS: Record<EnumJobStatus, string> = {
  [EnumJobStatus.Disabled]: "#660000",
  [EnumJobStatus.Wait]: "#cc5500",
  [EnumJobStatus.Queued]: "#365d6f",
  [EnumJobStatus.Preparing]: "#cc8400",
  [EnumJobStatus.RunWaiting]: "#009a9a",
  [EnumJobStatus.Running]: "#002eb8",
  [EnumJobStatus.Success]: "#29a329",
  [EnumJobStatus.Failed]: "#cc0000",
  [EnumJobStatus.Removed]: "#7a7a7a",
};

const STATUS_NAMES: Record<EnumJobStatus, string> = {
  [EnumJobStatus.Disabled]: "Отключено",
  [EnumJobStatus.Wait]: "Ожидание",
  [EnumJobStatus.Queued]: "В очереди",
  [EnumJobStatus.Preparing]: "Подготовка",
  [EnumJobStatus.RunWaiting]: "Ожидание запуска",
  [EnumJobStatus.Running]: "Выполняется",
  [EnumJobStatus.Success]: "Успешно",
  [EnumJobStatus.Failed]: "Ошибка",
  [EnumJobStatus.Removed]: "Удалено",
};

const props = defineProps<{
  tasks: JobTimeline[];
  startDate: Date;
  endDate: Date;
  timeZone?: string;
}>();

const emits = defineEmits<{
  "update:onlyError": [boolean];
  "task-clicked": [{ jobId: string; jobExecuteId: string | null }];
}>();

const canvas = ref<HTMLCanvasElement | null>(null);
const wrapper = ref<HTMLElement | null>(null);
const onlyError = ref(false);
const hoveredTask = ref<JobTimelineItem | null>(null);
const tooltipStyle = ref({ top: "0px", left: "0px" });
const activeTaskId = ref<string | null>(null);

// Зум
const zoomLevel = ref(1);
const baseHourWidth = 24;
const hourWidth = computed(() => baseHourWidth * zoomLevel.value);

// Высота канваса
const canvasHeight = ref(400);
const minCanvasHeight = computed(() => 70 + filteredTasks.value.length * 50);
const $popoverJobFilter = ref<TirPmPopover | null>(null);
const searchJob = ref("");
const jobs = ref<{ JobId: string; JobName: string; IsSelect: boolean }[]>([]);

const selectedJobs = computed(() => jobs.value.filter((item) => item.IsSelect));
const noSelectJobs = computed(() =>
  jobs.value.filter((item) => !item.IsSelect)
);

watch(
  () => props.tasks,
  () => updateJobs(props.tasks)
);
onMounted(() => updateJobs(props.tasks));

const updateJobs = (value: JobTimeline[]) => {
  jobs.value = value.map((item) => ({
    JobId: item.JobId,
    JobName: item.JobName,
    IsSelect: false,
  }));
};

const onSelectJob = (item: any) => {
  item.IsSelect = !item.IsSelect;
  onFilteredTasks();
  drawChart();
};

const onSearchJob = (value: string) => {
  jobs.value = props.tasks.filter((item) =>
    item.JobName.toLowerCase().includes(value.toLowerCase())
  );
};

const onOpenPopoverJobFilter = () => {
  console.log("onOpenPopoverJobFilter");
};

const onClosePopoverJobFilter = () => {
  console.log("onClosePopoverJobFilter");
};

const handleWheel = (event: WheelEvent) => {
  if (event.ctrlKey) {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    zoomLevel.value = Math.min(5, Math.max(0.5, zoomLevel.value + delta));
    drawChart();
  }
};

const zoomIn = () => {
  zoomLevel.value = Math.min(5, zoomLevel.value + 0.1);
  drawChart();
};

const zoomOut = () => {
  zoomLevel.value = Math.max(0.5, zoomLevel.value - 0.1);
  drawChart();
};

const filteredTasks = ref(props.tasks);

const onFilteredTasks = () => {
  if (!onlyError.value) {
    if (selectedJobs.value.length === 0) {
      filteredTasks.value = props.tasks;
    } else {
      filteredTasks.value = props.tasks.filter((task) =>
        selectedJobs.value.some((item) => item.JobId === task.JobId)
      );
    }
  } else {
    filteredTasks.value = props.tasks.filter((task) =>
      task.Executes.some((segment) => segment.Status === EnumJobStatus.Failed)
    );
  }
};

const daysRange = computed(() => {
  const start = new Date(props.startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(props.endDate);
  end.setHours(23, 59, 59, 999);
  const days = [];
  let current = new Date(start);

  while (current <= end) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return days;
});

const totalHours = computed(
  () => (props.endDate.getTime() - props.startDate.getTime()) / (1000 * 60 * 60)
);

const getStatusName = (status: EnumJobStatus) => {
  return STATUS_NAMES[status] || `Статус ${status}`;
};

const startResize = (event: MouseEvent) => {
  event.preventDefault();
  const startY = event.clientY;
  const startHeight = canvasHeight.value;

  document.body.style.cursor = "ns-resize";

  const onMouseMove = (moveEvent: MouseEvent) => {
    const newHeight = startHeight + (moveEvent.clientY - startY);
    canvasHeight.value = Math.max(200, newHeight);

    if (wrapper.value) {
      wrapper.value.style.height = `${canvasHeight.value}px`;
    }
    if (canvas.value) {
      canvas.value.height = canvasHeight.value;
    }
    drawChart();
  };

  const onMouseUp = () => {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "";
  };

  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);
};

const leftPadding = 180;

const drawChart = () => {
  if (!canvas.value || !wrapper.value) return;

  const ctx = canvas.value.getContext("2d");
  if (!ctx) return;

  const wrapperWidth = wrapper.value.clientWidth;
  const naturalWidth = daysRange.value.length * hourWidth.value * 24;
  const canvasWidth = Math.max(wrapperWidth, naturalWidth);
  const scaleFactor = canvasWidth / naturalWidth;

  const contentHeight = minCanvasHeight.value;
  canvas.value.width = canvasWidth;
  canvas.value.height = contentHeight;

  if (wrapper.value) {
    wrapper.value.style.height = `${Math.min(
      canvasHeight.value,
      contentHeight
    )}px`;
  }

  ctx.clearRect(0, 0, canvasWidth, contentHeight);

  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvasWidth, 70);

  daysRange.value.forEach((day, dayIndex) => {
    const dayX = leftPadding + dayIndex * hourWidth.value * 24 * scaleFactor;

    ctx.fillStyle = "#000";
    ctx.font = "bold 14px Arial";
    ctx.textAlign = "center";
    ctx.fillText(
      `${day.getDate().toString().padStart(2, "0")}.${(day.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`,
      dayX + (hourWidth.value * 24 * scaleFactor) / 2,
      20
    );

    for (let hour = 0; hour < 24; hour++) {
      const hourX = dayX + hour * hourWidth.value * scaleFactor;
      ctx.strokeStyle = "#eee";
      ctx.beginPath();
      ctx.moveTo(hourX, 70);
      ctx.lineTo(hourX, contentHeight);
      ctx.stroke();

      ctx.fillStyle = "#666";
      ctx.font = "12px Arial";
      ctx.fillText(
        hour.toString(),
        hourX + (hourWidth.value * scaleFactor) / 2,
        50
      );
    }

    ctx.strokeStyle = "#ccc";
    ctx.beginPath();
    ctx.moveTo(dayX, 0);
    ctx.lineTo(dayX, contentHeight);
    ctx.stroke();
  });

  filteredTasks.value.forEach((task, index) => {
    const taskY = 70 + index * 50;

    if (task.JobId === activeTaskId.value) {
      ctx.fillStyle = "rgba(0, 0, 255, 0.1)";
      ctx.fillRect(0, taskY, canvasWidth, 50);
    }

    ctx.fillStyle = "#000";
    ctx.font = "14px Arial";
    ctx.textAlign = "right";
    ctx.fillText(
      task.JobName.slice(0, 20) + (task.JobName.length > 20 ? "..." : ""),
      175,
      taskY + 15
    );

    ctx.strokeStyle = "#ccc";
    ctx.beginPath();
    ctx.moveTo(0, taskY);
    ctx.lineTo(canvasWidth, taskY);
    ctx.stroke();
  });

  filteredTasks.value.forEach((task, taskIndex) => {
    const y = 70 + taskIndex * 50 + 10;

    task.Executes.forEach((segment) => {
      const dateRun1 = new Date(segment.DateRun);
      const dateCom1 = segment.DateComplete
        ? new Date(segment.DateComplete)
        : new Date();
      const timezoneOffset = dateRun1.getTimezoneOffset();
      const dateRun = new Date(dateRun1.getTime() - timezoneOffset * 60 * 1000);
      const dateComplete = new Date(
        dateCom1.getTime() - timezoneOffset * 60 * 1000
      );

      const startOffsetHours =
        (dateRun.getTime() - props.startDate.getTime()) / (1000 * 60 * 60);
      const durationHours =
        (dateComplete.getTime() - dateRun.getTime()) / (1000 * 60 * 60);

      const startX = Math.max(
        leftPadding,
        startOffsetHours * hourWidth.value * scaleFactor
      );
      const widthBar = Math.max(
        Math.min(
          durationHours * hourWidth.value * scaleFactor,
          canvas.value.width - startX
        )
      );

      ctx.fillStyle = STATUS_COLORS[segment.Status] || "#ccc";
      ctx.fillRect(startX, y, widthBar, 30);
    });
  });
};

const onOnlyError = (value: boolean) => {
  onlyError.value = value;
  emits("update:onlyError", value);
  onFilteredTasks();
  drawChart();
};

const handleMouseMove = (event: MouseEvent) => {
  if (!canvas.value || !wrapper.value) return;

  const rect = canvas.value.getBoundingClientRect();
  const x = event.clientX - rect.left + wrapper.value.scrollLeft; // Убираем leftPadding из x
  const y = event.clientY - rect.top + wrapper.value.scrollTop;

  const wrapperWidth = wrapper.value.clientWidth;
  const naturalWidth = daysRange.value.length * hourWidth.value * 24;
  const scaleFactor = Math.max(wrapperWidth, naturalWidth) / naturalWidth;

  hoveredTask.value = null;

  filteredTasks.value.forEach((task, taskIndex) => {
    const taskY = 70 + taskIndex * 50;
    if (y >= taskY + 10 && y <= taskY + 40) {
      // Учитываем высоту сегмента (30px + отступ 10px)
      task.Executes.forEach((segment) => {
        const dateRun1 = new Date(segment.DateRun);
        const dateCom1 = segment.DateComplete
          ? new Date(segment.DateComplete)
          : new Date();
        const timezoneOffset = dateRun1.getTimezoneOffset();
        const dateRun = new Date(
          dateRun1.getTime() - timezoneOffset * 60 * 1000
        );
        const dateComplete = new Date(
          dateCom1.getTime() - timezoneOffset * 60 * 1000
        );

        const startOffsetHours =
          (dateRun.getTime() - props.startDate.getTime()) / (1000 * 60 * 60);
        const durationHours =
          (dateComplete.getTime() - dateRun.getTime()) / (1000 * 60 * 60);

        const startX = Math.max(
          leftPadding,
          startOffsetHours * hourWidth.value * scaleFactor
        );
        const widthBar = Math.min(
          durationHours * hourWidth.value * scaleFactor,
          canvas.value.width - startX
        );

        if (x >= startX && x <= startX + widthBar) {
          hoveredTask.value = segment;
          hoveredTask.value.JobName = task.JobName;
          tooltipStyle.value = {
            top: `${event.clientY - rect.top - 10}px`,
            left: `${
              event.clientX - rect.left + wrapper.value.scrollLeft + 20
            }px`, // Учитываем прокрутку
          };
        }
      });
    }
  });
};

const handleClick = (event: MouseEvent) => {
  if (!canvas.value || !wrapper.value) return;

  const rect = canvas.value.getBoundingClientRect();
  const x = event.clientX - rect.left + wrapper.value.scrollLeft; // Убираем лишний leftPadding
  const y = event.clientY - rect.top + wrapper.value.scrollTop;

  const wrapperWidth = wrapper.value.clientWidth;
  const naturalWidth = daysRange.value.length * hourWidth.value * 24;
  const scaleFactor = Math.max(wrapperWidth, naturalWidth) / naturalWidth;

  filteredTasks.value.forEach((task, taskIndex) => {
    const taskY = 70 + taskIndex * 50;
    if (y >= taskY + 10 && y <= taskY + 40) {
      // Учитываем высоту сегмента
      activeTaskId.value = task.JobId;
      let clickedJobExecuteId: string | null = null;

      task.Executes.forEach((segment) => {
        const dateRun1 = new Date(segment.DateRun);
        const dateCom1 = segment.DateComplete
          ? new Date(segment.DateComplete)
          : new Date();
        const timezoneOffset = dateRun1.getTimezoneOffset();
        const dateRun = new Date(
          dateRun1.getTime() - timezoneOffset * 60 * 1000
        );
        const dateComplete = new Date(
          dateCom1.getTime() - timezoneOffset * 60 * 1000
        );

        const startOffsetHours =
          (dateRun.getTime() - props.startDate.getTime()) / (1000 * 60 * 60);
        const durationHours =
          (dateComplete.getTime() - dateRun.getTime()) / (1000 * 60 * 60);

        const startX = Math.max(
          leftPadding,
          startOffsetHours * hourWidth.value * scaleFactor
        );
        const widthBar = Math.min(
          durationHours * hourWidth.value * scaleFactor,
          canvas.value.width - startX
        );

        console.log(
          `Task: ${task.JobName}, Segment: ${
            segment.JobExecuteId
          }, x: ${x}, startX: ${startX}, endX: ${startX + widthBar}`
        );

        if (x >= startX && x <= startX + widthBar) {
          clickedJobExecuteId = segment.JobExecuteId;
          console.log(`Clicked segment: ${segment.JobExecuteId}`);
        }
      });

      emits("task-clicked", {
        jobId: task.JobId,
        jobExecuteId: clickedJobExecuteId,
      });
      drawChart();
    }
  });
};

const handleMouseLeave = () => {
  hoveredTask.value = null;
};

onMounted(() => {
  nextTick(() => drawChart());
});

watch(
  () => [
    props.tasks,
    props.startDate,
    props.endDate,
    onlyError.value,
    zoomLevel.value,
  ],
  () => {
    nextTick(() => drawChart());
  },
  { deep: true }
);
</script>

<style scoped>
.gantt-chart {
  width: 100%;
  overflow-x: auto;
  font-family: Arial, sans-serif;
}

.gantt-wrapper {
  border-radius: 0.5rem;
  border: 1px solid #ccc;
  position: relative;
  overflow-y: auto;
  overflow-x: auto;
  max-height: 100%;
}

canvas {
  display: block;
}

.f {
  display: flex;
}
.aic {
  align-items: center;
}
.jcsb {
  justify-content: space-between;
}
.mb-8 {
  margin-bottom: 8px;
}
.mb-4 {
  margin-bottom: 4px;
}

.zoom-level {
  margin: 0 8px;
  font-size: 14px;
}

.tooltip {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 5px;
  pointer-events: none;
  z-index: 10;
}

.tooltip-color {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}

.resize-handle {
  width: 100%;
  height: 8px;
  background-color: #ddd;
  cursor: ns-resize;
  border-top: 1px solid #ccc;
  z-index: 1;
}

.resize-handle:hover {
  background-color: #ccc;
}

.gantt-wrapper {
  transition: height 0.2s ease;
}
</style>
